'use client'

import { userApi } from '@/shared/api/api'
import type { ApiRequestConfig } from '@/shared/api/axios'
import { COUPON_API_PATHS } from '@/shared/constants/clientApiPaths'
import type { ProductCouponState } from '@/shared/types/product.types'

export interface AvailableCoupon {
  id: string
  code: string
  headline: string
  description: string
  terms: string
  discount_type: string
  discount_value: number
  scope: string
  max_discount_cap: number
  min_cart_value: number
  applicable: boolean
  reason: string
  discount_amount: number
  shipping_discount: number
  message: string
  expires_at: string | null
  is_luxe: boolean
  /** Highlighted coupon for the checkout special-offer card. */
  is_special: boolean
  applied: boolean
}

export type AvailableCouponsResult = {
  cart_total: number
  coupons: AvailableCoupon[]
}

export interface ValidatedCoupon {
  valid: boolean
  coupon: AvailableCoupon | null
  eligible_total: number
  message: string
}

export interface CouponMutationResult {
  message: string
}
export interface ProductCouponApplyResult {
  state: ProductCouponState
  discount_amount: number
  measured_against: string
  message: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function toNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return 0
}

function toText(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function readExpiry(value: Record<string, unknown>): string | null {
  const raw = value.expires_at
  return typeof raw === 'string' && raw.trim() ? raw : null
}

function fallbackHeadline(value: Record<string, unknown>): string {
  const headline = toText(value.headline)
  if (headline) return headline

  const discountType = toText(value.discount_type)
  const discountValue = toNumber(value.discount_value)
  if (discountType === 'percentage' && discountValue > 0) return `${discountValue}% OFF`
  if (discountValue > 0) return `₹${discountValue} OFF`
  return toText(value.code)
}

function fallbackDescription(value: Record<string, unknown>): string {
  const description = toText(value.description)
  if (description) return description

  const discountType = toText(value.discount_type)
  const discountValue = toNumber(value.discount_value)
  const cap = toNumber(value.max_discount_cap)
  const min = toNumber(value.min_cart_value)
  if (discountType === 'percentage' && discountValue > 0) {
    const capText = cap > 0 ? ` upto ₹${cap}` : ''
    const minText = min > 0 ? ` on purchases above ₹${min}` : ''
    return `Get ${discountValue}% off${capText}${minText}`
  }
  if (discountValue > 0) return `Save ₹${discountValue}`
  return ''
}

function normalizeCoupon(value: unknown, applied = false): AvailableCoupon | null {
  if (!isRecord(value)) return null

  const code = typeof value.code === 'string' ? value.code : ''
  if (!code) return null

  return {
    id: toText(value.id, code),
    code,
    headline: fallbackHeadline(value),
    description: fallbackDescription(value),
    terms: toText(value.terms, 'T&C Apply'),
    discount_type: toText(value.discount_type),
    discount_value: toNumber(value.discount_value),
    scope: toText(value.scope),
    max_discount_cap: toNumber(value.max_discount_cap),
    min_cart_value: toNumber(value.min_cart_value),
    applicable: Boolean(value.applicable),
    reason: toText(value.reason),
    discount_amount: toNumber(value.discount_amount),
    shipping_discount: toNumber(value.shipping_discount),
    message: toText(value.message),
    expires_at: readExpiry(value),
    is_luxe: value.is_luxe === true,
    is_special: value.is_special === true,
    applied: value.applied === true || applied,
  }
}

function normalizeAvailableCouponsResponse(payload: unknown): AvailableCouponsResult {
  if (!isRecord(payload)) {
    return { cart_total: 0, coupons: [] }
  }

  const data = isRecord(payload.data) ? payload.data : payload
  const rawCoupons = data.coupons

  const coupons = Array.isArray(rawCoupons)
    ? rawCoupons
        .map((coupon) => normalizeCoupon(coupon))
        .filter((coupon): coupon is AvailableCoupon => coupon !== null)
    : []

  return { cart_total: toNumber(data.cart_total), coupons }
}

function getAvailableCouponsConfig(skipToast = false): ApiRequestConfig {
  return {
    headers: {
      'Content-Type': 'application/json',
      skipToast: true,
    },
  }
}

export async function fetchAvailableCoupons(
  skipToast = false,
): Promise<AvailableCouponsResult> {
  const response = await userApi.get<unknown>(
    COUPON_API_PATHS.available,
    getAvailableCouponsConfig(skipToast),
  )

  return normalizeAvailableCouponsResponse(response)
}

export interface GuestCouponLine {
  productId: string
  variantId?: string | null
  quantity: number
}

/** The coupon a guest asked for, as priced against the posted basket. */
export interface GuestAppliedCoupon {
  code: string
  applicable: boolean
  /** Null when not applicable — the backend sends only `code` and `reason` then. */
  coupon: AvailableCoupon | null
  reason: string
}

export type GuestCouponsResult = AvailableCouponsResult & {
  applied: GuestAppliedCoupon | null
}

function normalizeGuestApplied(value: unknown): GuestAppliedCoupon | null {
  if (!isRecord(value)) return null

  const code = toText(value.code)
  if (!code) return null

  const applicable = value.applicable === true
  return {
    code,
    applicable,
    coupon: applicable ? normalizeCoupon(value, true) : null,
    reason: toText(value.reason),
  }
}

/**
 * Signed-out cart: `/coupons/guest` is public and stateless, so the basket
 * travels in the body. Never send a price — the server resolves it from
 * `productId`. A `code` that doesn't hold still comes back 200, with
 * `applied.applicable === false` and a `reason`.
 */
export async function fetchGuestCoupons(
  lines: GuestCouponLine[],
  code?: string | null,
): Promise<GuestCouponsResult> {
  const items = lines.map((line) => ({
    productId: line.productId,
    quantity: line.quantity,
    ...(line.variantId ? { variantId: line.variantId } : {}),
  }))

  const response = await userApi.post<unknown>(
    COUPON_API_PATHS.guest,
    { items, ...(code ? { code } : {}) },
    { ...getAvailableCouponsConfig(), authTokenType: false },
  )

  const data = isRecord(response) && isRecord(response.data) ? response.data : response
  return {
    ...normalizeAvailableCouponsResponse(response),
    applied: isRecord(data) ? normalizeGuestApplied(data.applied) : null,
  }
}

function normalizeValidatedCouponResponse(payload: unknown, applied: boolean): ValidatedCoupon {
  if (!isRecord(payload)) {
    return {
      valid: false,
      coupon: null,
      eligible_total: 0,
      message: 'Invalid coupon',
    }
  }

  const rawData = isRecord(payload.data) ? payload.data : payload
  const valid = Boolean(rawData.valid)
  const rawCoupon = isRecord(rawData.coupon) ? rawData.coupon : null
  const code = typeof rawCoupon?.code === 'string' ? rawCoupon.code : ''

  const coupon = valid && code
    ? normalizeCoupon(
        {
          ...(rawCoupon ?? {}),
          id: rawCoupon?.id,
          code,
          discount_type: rawCoupon?.discount_type ?? rawData.discount_type,
          discount_value: rawData.discount_value,
          max_discount_cap: rawData.max_discount_cap,
          min_cart_value: rawData.min_cart_value,
          discount_amount: rawData.discount_amount,
          shipping_discount: rawData.shipping_discount,
          message: toText(rawData.message, 'Coupon valid'),
          applicable: true,
          reason: '',
        },
        applied,
      )
    : null

  return {
    valid,
    coupon,
    eligible_total: toNumber(rawData.eligible_total),
    message: typeof rawData.message === 'string' ? rawData.message : 'Invalid coupon',
  }
}

export async function validateCoupon(code: string): Promise<ValidatedCoupon> {
  const response = await userApi.post<unknown>(
    COUPON_API_PATHS.validate,
    { code },
    getAvailableCouponsConfig(),
  )

  return normalizeValidatedCouponResponse(response, false)
}

export async function applyCoupon(code: string): Promise<ValidatedCoupon> {
  const response = await userApi.post<unknown>(
    COUPON_API_PATHS.apply,
    { code },
    getAvailableCouponsConfig(),
  )

  return normalizeValidatedCouponResponse(response, true)
}

const PRODUCT_COUPON_STATES: readonly ProductCouponState[] = ['available', 'armed', 'applied']

function normalizeProductCouponApply(payload: unknown): ProductCouponApplyResult {
  const data = isRecord(payload) && isRecord(payload.data) ? payload.data : payload

  if (!isRecord(data)) {
    return {
      state: 'applied',
      discount_amount: 0,
      measured_against: 'product',
      message: 'Coupon applied',
    }
  }

  return {
    state: PRODUCT_COUPON_STATES.includes(data.state as ProductCouponState)
      ? (data.state as ProductCouponState)
      : 'applied',
    discount_amount: toNumber(data.discount_amount),
    measured_against: toText(data.measured_against, 'product'),
    message: toText(data.message, 'Coupon applied'),
  }
}

export async function applyProductCoupon(
  code: string,
  productId: string,
): Promise<ProductCouponApplyResult> {
  const response = await userApi.post<unknown>(
    COUPON_API_PATHS.apply,
    { code, productId },
    getAvailableCouponsConfig(),
  )

  return normalizeProductCouponApply(response)
}

function normalizeCouponMutationResult(payload: unknown): CouponMutationResult {
  if (!isRecord(payload)) {
    return { message: 'Coupon removed successfully!' }
  }

  const message =
    typeof payload.message === 'string'
      ? payload.message
      : isRecord(payload.data) && typeof payload.data.message === 'string'
        ? payload.data.message
        : 'Coupon removed successfully!'

  return { message }
}

export async function removeCoupon(): Promise<CouponMutationResult> {
  const response = await userApi.delete<unknown>(
    COUPON_API_PATHS.remove,
    getAvailableCouponsConfig(),
  )

  return normalizeCouponMutationResult(response)
}
